import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import LaunchIcon from '@material-ui/icons/Launch';
import PlaceIcon from '@material-ui/icons/Place';
import { statesItems } from '../wikiutil';
import { RootState } from '../../../redux/root';
import { setSelectedState } from '../../../redux/slices/wikiSlice';
import { useGetVocabularyCountyQuery } from '../../../redux/rtkQuery/apiSlice';
import Select from 'react-select';
import ModalDefaultDraggable from '../../../molecules/ModalDefaultDraggable';
import openInNewTab from '../../../utils/openInNewTab';

const FL = { id: 'FL', name: 'Florida', title: 'Florida' };

const PropertyAppraiserLookup = () => {
  const dispatch = useDispatch();
  const selectedState = useSelector((s: RootState) => s.wikiSlice.selectedState);
  const { data, isLoading } = useGetVocabularyCountyQuery({});
  const [isOpen, setIsOpen] = useState(false);
  const [county, setCounty] = useState<any>(null);
  const [countyFilter, setCountyFilter] = useState('');

  useEffect(() => {
    if (!selectedState?.id) dispatch(setSelectedState(FL));
  }, []);

  const counties = useMemo(() => {
    if (!data?.results || !selectedState?.id) return [];
    return data.results.filter(c => c.state === selectedState.id);
  }, [data, selectedState]);

  const filteredCounties = useMemo(() => {
    const q = countyFilter.trim().toLowerCase();
    if (!q) return counties;
    return counties.filter(c => (c.name || '').toLowerCase().includes(q));
  }, [counties, countyFilter]);

  const onStateChanged = e => {
    dispatch(setSelectedState(e));
    setCounty(null);
    setCountyFilter('');
  };

  const openModal = () => {
    setCounty(null);
    setCountyFilter('');
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const url = county?.websitePropertyAppraiser;

  return (
    <>
      <Trigger>
        <InlineLabel>State</InlineLabel>
        <InlineSelect>
          <Select
            isLoading={isLoading}
            isClearable={false}
            menuPlacement="auto"
            classNamePrefix="react-select"
            getOptionValue={(item: any) => item.id || Math.random()}
            getOptionLabel={(item: any) => item.name}
            options={statesItems}
            onChange={onStateChanged}
            value={selectedState}
            defaultValue={FL}
            styles={inlineSelectStyles}
          />
        </InlineSelect>
        <SearchButton type="button" onClick={openModal}>
          <SearchIcon style={{ fontSize: 18 }} />
          Search Your Property Appraiser Website
        </SearchButton>
      </Trigger>

      {isOpen && (
        <ModalDefaultDraggable
          centered
          maxHeight="640px"
          onClose={closeModal}
          title="Property Appraiser Lookup"
        >
          <ModalBody>
            <Subtitle>
              Choose a state and county to find the official Property Appraiser website.
            </Subtitle>

            <FieldRow>
              <Field>
                <FieldLabel>State</FieldLabel>
                <FieldControl>
                  <Select
                    isLoading={isLoading}
                    isClearable={false}
                    menuPlacement="auto"
                    classNamePrefix="react-select"
                    placeholder="Select state"
                    getOptionValue={(item: any) => item.id || Math.random()}
                    getOptionLabel={(item: any) => item.name}
                    options={statesItems}
                    onChange={onStateChanged}
                    value={selectedState}
                    defaultValue={FL}
                    styles={inlineSelectStyles}
                  />
                </FieldControl>
              </Field>
              <Field>
                <FieldLabel>Search County</FieldLabel>
                <SearchInputWrap>
                  <SearchIcon style={{ fontSize: 18, color: 'var(--neutral-400)' }} />
                  <SearchInput
                    placeholder="Type county name…"
                    value={countyFilter}
                    onChange={e => setCountyFilter(e.target.value)}
                  />
                </SearchInputWrap>
              </Field>
            </FieldRow>

            <ListHeader>
              <ListEyebrow>Counties</ListEyebrow>
              <ListCount>
                {filteredCounties.length}
                {filteredCounties.length === 1 ? ' result' : ' results'}
              </ListCount>
            </ListHeader>

            <CountyList>
              {filteredCounties.length === 0 && (
                <EmptyState>
                  {isLoading ? 'Loading counties…' : 'No counties match that search.'}
                </EmptyState>
              )}
              {filteredCounties.map(c => {
                const isActive = county?.id === c.id;
                const hasUrl = !!c.websitePropertyAppraiser;
                return (
                  <CountyRow
                    key={c.id}
                    active={isActive}
                    onClick={() => setCounty(c)}
                  >
                    <PlaceIcon
                      style={{
                        fontSize: 18,
                        color: isActive ? 'var(--primary-600)' : 'var(--neutral-500)',
                      }}
                    />
                    <CountyName>{c.name}</CountyName>
                    {hasUrl ? (
                      <UrlBadge>Available</UrlBadge>
                    ) : (
                      <UrlBadgeMuted>No link</UrlBadgeMuted>
                    )}
                  </CountyRow>
                );
              })}
            </CountyList>

            <ResultPanel>
              <ResultEyebrow>Property Appraiser Website</ResultEyebrow>
              {county ? (
                url ? (
                  <ResultBody>
                    <ResultUrl title={url}>{url}</ResultUrl>
                    <OpenButton type="button" onClick={() => openInNewTab(url)}>
                      <LaunchIcon style={{ fontSize: 18 }} />
                      Open Website
                    </OpenButton>
                  </ResultBody>
                ) : (
                  <ResultEmpty>
                    No Property Appraiser website is on file for {county.name}.
                  </ResultEmpty>
                )
              ) : (
                <ResultEmpty>Select a county above to view its website.</ResultEmpty>
              )}
            </ResultPanel>
          </ModalBody>
        </ModalDefaultDraggable>
      )}
    </>
  );
};

export default PropertyAppraiserLookup;

const Trigger = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  box-sizing: border-box;

  > * {
    align-self: center;
  }
`;

const InlineLabel = styled.label`
  display: inline-flex;
  align-items: center;
  height: 40px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-700);
  white-space: nowrap;
  margin: 0;
`;

const InlineSelect = styled.div`
  width: 220px;
  height: 40px;
  display: flex;
  align-items: center;

  > div {
    width: 100%;
  }
`;

const inlineSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? 'var(--primary-600)' : 'var(--border)',
    boxShadow: state.isFocused ? 'var(--shadow-focus)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--primary-600)' : 'var(--neutral-400)',
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: 40,
    padding: '0 12px',
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: 40,
  }),
  singleValue: (base: any) => ({
    ...base,
    fontSize: '0.875rem',
    color: 'var(--mainText)',
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: '0.875rem',
    zIndex: 5,
  }),
};

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
  flex: 1 1 240px;
`;

const FieldLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--neutral-700);
  letter-spacing: 0.01em;
`;

const FieldControl = styled.div`
  width: 100%;

  > div {
    width: 100%;
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const Subtitle = styled.p`
  margin: -4px 0 0;
  font-size: 0.875rem;
  color: var(--neutral-600);
  line-height: 1.45;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const SearchInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  color: var(--mainText);

  &::placeholder {
    color: var(--neutral-400);
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
`;

const ListEyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const ListCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 10px;
`;

const CountyList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  max-height: 240px;
  overflow-y: auto;
`;

const CountyRow = styled.button<{ active: boolean }>`
  display: grid;
  grid-template-columns: 18px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid var(--border);
  background: ${({ active }) => (active ? 'var(--primary-50)' : 'transparent')};
  color: var(--mainText);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ active }) => (active ? 'var(--primary-100)' : 'var(--neutral-50)')};
  }
`;

const CountyName = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UrlBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary-700);
  background: var(--primary-50);
  border: 1px solid var(--primary-100);
  border-radius: 999px;
  padding: 2px 10px;
`;

const UrlBadgeMuted = styled(UrlBadge)`
  color: var(--neutral-500);
  background: var(--neutral-100);
  border-color: var(--border);
`;

const EmptyState = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const ResultPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
`;

const ResultEyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const ResultBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`;

const ResultUrl = styled.a`
  flex: 1 1 240px;
  min-width: 0;
  font-size: 0.875rem;
  color: var(--primary-700);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ResultEmpty = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
`;
